import React, { useState } from 'react';
import { fetchApi, endpoints } from '../../utils/api';
import PostSubmit from './post-submit';
import LoadingModal from '../bonus/api-loading-modal';
import InvestorsRotating from '../common/investors-rotating.js';
import HoneypotField from './honeypot-field';
import { CHAR_LIMITS, getRemainingChars } from '../../utils/constants';
import Seo from '../common/seo';

function Investors() {
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        company: '',
        email: '',
        linkedin: '',
        userSubmittedNotes: '',
        xProfileUrl: '',
        estCheckSize: '',
        website: '' // honeypot — stays empty for real users
    });
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const [error, setError] = useState(null);
    const [submitAttempted, setSubmitAttempted] = useState(false);

    const handleChange = (e) => {
        const { id, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [id]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitAttempted(true);
        setIsOpen(true);
        
        try {
            await fetchApi(endpoints.submitInvestor(), {
                method: 'POST',
                body: JSON.stringify({
                    first_name: formData.firstName,
                    last_name: formData.lastName,
                    company: formData.company,
                    email: formData.email,
                    linkedin: formData.linkedin,
                    user_submitted_notes: formData.userSubmittedNotes,
                    x_profile_url: formData.xProfileUrl,
                    est_check_size: formData.estCheckSize,
                    submission_type: 'website',
                    status: 'new',
                    website: formData.website
                }),
            });

            setIsSubmitted(true);
        } catch (error) {
            console.error('Error submitting form:', error);
            setError({
                message: error.message,
                source: 'investor',
                time: new Date().toISOString()
            });
        } finally {
            setIsOpen(false);
        }
    };

    if (isSubmitted) {
        return <PostSubmit />;
    }

    return (
        <main className="container">
            <Seo path="/investors" />
            <LoadingModal isOpen={isOpen} message="1 Sec. The Server is Cooking..." />
            <div className="form-container">
                <h1 className="page-title">INVESTORS/ANGELS</h1>
                <p className="info-text">Every 3 months, we fund 5-7 new teams.</p>
                <p className="info-text">We fund the top 1% of applicants.</p>
                <p className="info-text">70%+ of LY teams raise follow-on capital. $1.5M on average.</p>
                <p className="info-text">Submit below to get an early look.</p>
                <hr className="light-divider" aria-hidden="true"></hr>
                <InvestorsRotating />
                <hr className="light-divider" aria-hidden="true"></hr>
                <h2>Join Investor Network</h2>
                <form className={`application-form ${submitAttempted ? 'form-submitted' : ''}`} onSubmit={handleSubmit}>
                    <HoneypotField value={formData.website} onChange={handleChange} />
                    <div className="form-group">
                        <label htmlFor="firstName" className="info-text">
                            First Name
                        </label>
                        <input
                            type="text"
                            id="firstName"
                            className="form-input"
                            required
                            placeholder="..."
                            value={formData.firstName}
                            onChange={handleChange}
                            maxLength={CHAR_LIMITS.INVESTOR_FIRST_NAME}
                        />
                        <div className={getRemainingChars(formData.firstName, CHAR_LIMITS.INVESTOR_FIRST_NAME).className}>
                            {getRemainingChars(formData.firstName, CHAR_LIMITS.INVESTOR_FIRST_NAME).remaining} characters remaining
                        </div>
                    </div>
                    <div className="form-group">
                        <label htmlFor="lastName" className="info-text">
                            Last Name
                        </label>
                        <input
                            type="text"
                            id="lastName"
                            className="form-input"
                            required
                            placeholder="..."
                            value={formData.lastName}
                            onChange={handleChange}
                            maxLength={CHAR_LIMITS.INVESTOR_LAST_NAME}
                        />
                        <div className={getRemainingChars(formData.lastName, CHAR_LIMITS.INVESTOR_LAST_NAME).className}>
                            {getRemainingChars(formData.lastName, CHAR_LIMITS.INVESTOR_LAST_NAME).remaining} characters remaining
                        </div>
                    </div>
                    <div className="form-group">
                        <label htmlFor="company" className="info-text">
                            Company/Fund
                        </label>
                        <input
                            type="text"
                            id="company"
                            className="form-input"
                            required
                            placeholder="...or if an angel, just put angel"
                            value={formData.company}
                            onChange={handleChange}
                            maxLength={CHAR_LIMITS.INVESTOR_COMPANY}
                        />
                        <div className={getRemainingChars(formData.company, CHAR_LIMITS.INVESTOR_COMPANY).className}>
                            {getRemainingChars(formData.company, CHAR_LIMITS.INVESTOR_COMPANY).remaining} characters remaining
                        </div>
                    </div>
                    <div className="form-group">
                        <label htmlFor="email" className="info-text">
                            Email
                        </label>
                        <input
                            type="email"
                            id="email"
                            className="form-input"
                            required
                            placeholder="so we can contact you"
                            value={formData.email}
                            onChange={handleChange}
                            maxLength={CHAR_LIMITS.INVESTOR_EMAIL}
                        />
                        <div className={getRemainingChars(formData.email, CHAR_LIMITS.INVESTOR_EMAIL).className}>
                            {getRemainingChars(formData.email, CHAR_LIMITS.INVESTOR_EMAIL).remaining} characters remaining
                        </div>
                    </div>
                    <div className="form-group">
                        <label htmlFor="linkedin" className="info-text">
                            LinkedIn Profile
                        </label>
                        <input
                            type="text"
                            id="linkedin"
                            className="form-input"
                            required
                            placeholder="your LinkedIn profile URL"
                            value={formData.linkedin}
                            onChange={handleChange}
                            maxLength={CHAR_LIMITS.INVESTOR_LINKEDIN}
                        />
                        <div className={getRemainingChars(formData.linkedin, CHAR_LIMITS.INVESTOR_LINKEDIN).className}>
                            {getRemainingChars(formData.linkedin, CHAR_LIMITS.INVESTOR_LINKEDIN).remaining} characters remaining
                        </div>
                    </div>
                    <div className="form-group">
                        <label htmlFor="xProfileUrl" className="info-text">
                            X Profile (Optional)
                        </label>
                        <input
                            type="text"
                            id="xProfileUrl"
                            className="form-input"
                            placeholder="your X/Twitter profile URL"
                            value={formData.xProfileUrl}
                            onChange={handleChange}
                            maxLength={CHAR_LIMITS.INVESTOR_X_PROFILE}
                        />
                        <div className={getRemainingChars(formData.xProfileUrl, CHAR_LIMITS.INVESTOR_X_PROFILE).className}>
                            {getRemainingChars(formData.xProfileUrl, CHAR_LIMITS.INVESTOR_X_PROFILE).remaining} characters remaining
                        </div>
                    </div>
                    <div className="form-group">
                        <label htmlFor="estCheckSize" className="info-text">
                            Typical Check Size
                        </label>
                        <select
                            id="estCheckSize"
                            className="form-input"
                            required
                            value={formData.estCheckSize}
                            onChange={handleChange}
                        >
                            <option value="">Select check size range...</option>
                            <option value="Under $20k">Small Angel (&lt;$20k)</option>
                            <option value="Under $100k">Active Angel (&lt;$100k)</option>
                            <option value="$100-500k">$100-500k</option>
                            <option value="$500k+">$500k+</option>
                        </select>
                        <div className="char-counter" style={{ visibility: 'hidden' }}>
                            &nbsp;
                        </div>
                    </div>
                    <div className="form-group">
                        <label htmlFor="userSubmittedNotes" className="info-text">
                            Extra Info (Optional)
                        </label>
                        <textarea
                            id="userSubmittedNotes"
                            className="form-input"
                            placeholder="share anything thats top of mind"
                            value={formData.userSubmittedNotes}
                            onChange={handleChange}
                            maxLength={CHAR_LIMITS.INVESTOR_NOTES}
                        />
                        <div className={getRemainingChars(formData.userSubmittedNotes, CHAR_LIMITS.INVESTOR_NOTES).className}>
                            {getRemainingChars(formData.userSubmittedNotes, CHAR_LIMITS.INVESTOR_NOTES).remaining} characters remaining
                        </div>
                    </div>
                    {error && (
                        <div className="error-submit-container">
                            <h1 className="page-title">Ah Sh*t...</h1>
                            <p className="info-text">I'm sorry. We probably wrote some bad code.</p>
                            <p className="info-text">If you got this. Feel free to copy your info and email us at build@takeleapyear.com.</p>
                            <p className="info-text">We will review & get back to you soon.</p>
                            <p className="info-text">Sincerely, the LeapYear Team</p>
                            <button 
                                type="button"
                                className="try-again-btn"
                                onClick={() => setError(null)}
                            >
                                Dismiss Error
                            </button>
                        </div>
                    )}
                    <button type="submit" className="submit-button">
                        Submit
                    </button>
                </form>
            </div>
        </main>
    );
}

export default Investors; 